import { z } from 'zod';
import { isValidTimeZone } from './zones.js';
import { addDaysIso, zonedDate, zonedParts, zonedToUtc } from './time.js';
/**
 * Times written in chat ("2:00 pm", "tomorrow at 14:30") are found when the message is sent and saved
 * with it as an exact moment (docs/adr/0073-time-mentions.md). Readers see the words as typed; hovering
 * shows the moment in their own zone. The moment is fixed at send time, so it stays right if anyone
 * later changes their time zone.
 */
export const MAX_TIME_MENTIONS = 10;
export const TimeMention = z.object({
    /** Where the typed words sit in the message body (UTF-16 offsets, end exclusive). */
    start: z.number().int().min(0).max(10000),
    end: z.number().int().min(1).max(10000),
    /** The words as typed, e.g. "2:00 pm". Must match the body at start..end. */
    text: z.string().min(1).max(40),
    /** The exact moment, ISO 8601 in UTC. */
    startsAt: z.iso.datetime(),
    /** The sender's zone when it was sent ("Asia/Karachi"). */
    tz: z.string().refine(isValidTimeZone, { message: 'Unknown time zone' }),
});
export const TimeMentions = z.array(TimeMention).max(MAX_TIME_MENTIONS);
const AMPM = /^(\d{1,2})(?::(\d{2}))?\s?([ap])\.?m\.?$/i;
const H24 = /^(\d{1,2}):(\d{2})$/;
/** "2:00 pm", "2pm", "9:30 AM", "14:30" → hour and minute (24 h). Null when it is not a clear time. */
export function parseClock(text) {
    const t = text.trim();
    let m = AMPM.exec(t);
    if (m) {
        const h = Number(m[1]);
        const min = m[2] ? Number(m[2]) : 0;
        if (h < 1 || h > 12 || min > 59)
            return null;
        return { hour: (h % 12) + (m[3].toLowerCase() === 'p' ? 12 : 0), minute: min };
    }
    m = H24.exec(t);
    if (m) {
        const h = Number(m[1]);
        const min = Number(m[2]);
        if (h > 23 || min > 59)
            return null;
        return { hour: h, minute: min };
    }
    return null;
}
// A clear clock time: "2pm", "2:00 pm", "9:30am", or 24-hour with a colon ("14:30"). A bare number
// ("at 5", "version 2.0", "5 items") is never a time. Digits, colons or dots just before are rejected.
const CLOCK = /(?<![\d:.])(?:(?:1[0-2]|0?[1-9])(?::[0-5]\d)?\s?[ap]\.?m\.?|(?:[01]?\d|2[0-3]):[0-5]\d)(?![\d:])(?!\.\d)/gi;
// Places where a time is just text: code, **bold** (it is drawn as one piece), links, mentions.
const SKIP = /`[^`]*`|\*\*[^*]+\*\*|https?:\/\/[^\s<]+|<@[0-9a-f-]{36}>/gi;
const DAY_WORDS = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
const DAY_BEFORE = /\b(today|tonight|tomorrow|(?:on\s+)?(sun|mon|tue|wed|thu|fri|sat)[a-z]*)\b[\s,]*(?:at\s+|@\s*)?$/i;
const DAY_AFTER = /^\s*(?:,\s*)?(today|tomorrow)\b/i;
/** Every clear time in `body`, left to right. Code, links and mentions are skipped. */
export function findTimes(body) {
    const skip = [];
    for (const m of body.matchAll(SKIP))
        skip.push([m.index, m.index + m[0].length]);
    const out = [];
    for (const m of body.matchAll(CLOCK)) {
        const start = m.index;
        const end = start + m[0].length;
        if (skip.some(([a, b]) => start < b && end > a))
            continue;
        if (!parseClock(m[0]))
            continue;
        out.push({ start, end, text: m[0] });
        if (out.length >= MAX_TIME_MENTIONS)
            break;
    }
    return out;
}
/**
 * The moment a typed time means for a sender in `tz`. A day word next to it ("tomorrow", "Friday") sets the
 * day; with none, it is the next time the clock reads that, today if still ahead, otherwise tomorrow.
 */
export function momentFor(body, found, tz, now = new Date()) {
    const clock = parseClock(found.text);
    if (!clock || !isValidTimeZone(tz))
        return null;
    const today = zonedDate(now, tz);
    const nowParts = zonedParts(now, tz);
    const nowMinutes = nowParts.hour * 60 + nowParts.minute;
    const wantMinutes = clock.hour * 60 + clock.minute;
    const before = DAY_BEFORE.exec(body.slice(Math.max(0, found.start - 24), found.start));
    const after = DAY_AFTER.exec(body.slice(found.end, found.end + 16));
    const word = (before?.[1] ?? after?.[1] ?? '').toLowerCase();
    let date = today;
    if (word === 'tomorrow')
        date = addDaysIso(today, 1);
    else if (word === 'today' || word === 'tonight')
        date = today;
    else if (before?.[2]) {
        const want = DAY_WORDS[before[2].toLowerCase().slice(0, 3)];
        let ahead = (want - nowParts.weekday + 7) % 7;
        if (ahead === 0 && wantMinutes <= nowMinutes)
            ahead = 7;
        date = addDaysIso(today, ahead);
    }
    else if (wantMinutes <= nowMinutes)
        date = addDaysIso(today, 1);
    const [y, mo, d] = date.split('-').map(Number);
    return zonedToUtc(y, mo, d, clock.hour, clock.minute, tz);
}
/** What the composer saves: every clear time in `body` with its exact moment for a sender in `tz`. */
export function timeMentionsFor(body, tz, now = new Date()) {
    const out = [];
    for (const f of findTimes(body)) {
        const at = momentFor(body, f, tz, now);
        if (at)
            out.push({ start: f.start, end: f.end, text: f.text, startsAt: at.toISOString(), tz });
    }
    return out;
}
/**
 * Server check: the words really sit in the body, the zone is real, the clock in that zone reads what was
 * typed, and the moment is near. Returns an error message, or null when the record is fine.
 */
export function checkTimeMentions(body, mentions, now = new Date()) {
    let last = 0;
    for (const t of mentions) {
        if (t.end > body.length || t.start < last || body.slice(t.start, t.end) !== t.text)
            return 'A time does not match the message';
        last = t.end;
        const clock = parseClock(t.text);
        if (!clock)
            return 'A time is not a clear time';
        const at = new Date(t.startsAt);
        const p = zonedParts(at, t.tz);
        if (p.hour !== clock.hour || p.minute !== clock.minute)
            return 'A time does not match its zone';
        const ms = at.getTime() - now.getTime();
        if (Math.abs(ms) > 400 * 86_400_000)
            return 'A time is too far from now';
    }
    return null;
}
//# sourceMappingURL=time-mentions.js.map